<template>
  <VCard v-if="header || loyaltyMember" class="px-6" variant="flat" :class="{'header-notification py-4' : header}">
    <SLoadingOverlay :loading="status === 'pending'" />
    <!-- Only once the balance has loaded: a failed fetch would leave the labels with no amounts. -->
    <template v-if="loyaltyMember && status === 'success'">
      <VRow no-gutters class="font-weight-bold">
        <VIcon class="pr-2" size="28" color="rgb(var(--v-theme-primary))">
          {{ icons.mdiGiftOutline }}
        </VIcon>
        <VCol cols="auto" align-self="center">
          {{ $t('label.stampinRewards') }}:
        </VCol>
        <VCol class="text-right ml-12" align-self="center">
          {{ formattedAvailable }}
        </Vcol>
      </VRow>
      <VRow v-if="expiringSoon" no-gutters>
        <VIcon class="pr-2" size="28" color="rgb(var(--v-theme-primary))">
          {{ icons.mdiClockAlertOutline }}
        </VIcon>
        <VCol cols="auto" align-self="center">
          {{ $t('label.loyalty.expiringSoon') }}:
        </VCol>
        <VCol class="text-right text-secondary" align-self="center">
          {{ formattedExpiringSoon }}
        </VCol>
      </VRow>
    </template>
    <VRow v-if="header && !loyaltyMember" class="v2-gutters">
      <VCol class="text-secondary font-weight-bold text-center font18">
        {{ $t('p.rewardsPlug') }}
      </VCol>
    </VRow>
    <VDivider v-if="header" class="rewards-divider" />
    <VRow v-if="header" class="v2-gutters mt-0">
      <VCol class="text-right font-weight-medium">
        <span v-if="loyaltyMember" @click="emits('close')">
          <NuxtLink to="/account/rewards">
            {{ loyaltyMember ? $t('input.label.viewDetails') : $t('rewards.btn.learnMore') }}
          </NuxtLink>
        </span>
        <VHtml v-else :html="$t('rewards.btn.learnMore')" />
      </VCol>
    </VRow>
  </VCard>
</template>

<script setup lang="ts">
import { mdiGiftOutline, mdiClockAlertOutline } from '@mdi/js'
import { useAccountStore } from '~/stores/account'
import VHtml from '~/components/base/VHtml.vue'
import { UserRole } from '~/types/account'

const icons = { mdiGiftOutline, mdiClockAlertOutline }
const accountStore = useAccountStore()


interface Props {
  header?: boolean
}

withDefaults(defineProps<Props>(), {
  header: false
})

const emits = defineEmits<{
  (e: 'close'): void
}>()

const { user, userRole } = useSAuthState()
const loyaltyDashboard = computed(() => accountStore.loyaltyDashboard)
const formattedAvailable = computed(() => loyaltyDashboard.value.balance?.formattedCurrentBalance)
const formattedExpiringSoon = computed(() => loyaltyDashboard.value.expiring?.expiringGroups[0]?.formattedAmount)
const expiringSoon = computed(() => loyaltyDashboard.value.expiring?.expiringGroups[0]?.amount)
const loyaltyMember = computed(() => user.value.loyaltyOptIn)

const { status } = useAsyncData('fetch-loyalty-points', async () => {
  if (!accountStore.loyaltyDashboard.balance && [UserRole.Customer, UserRole.Demonstrator].includes(userRole.value)) {
    await accountStore.fetchLoyaltyInventory()
  }
  return true
})
</script>

<style lang="scss" scoped>
@use '~/assets/scss/settings';
@use 'sass:map';

.v-card.header-notification {
  position: absolute;
  box-shadow: 0 3px 6px #00000062;
  min-width: 320px;
  max-width: 350px;
  top: 92px;
  right: 0;
  border-radius: 0 0 8px 8px;
  @media #{map.get(settings.$display-breakpoints, 'lg-and-down')} {
    right: 20px;
  }
}
.rewards-divider {
  border-color: rgb(var(--v-theme-border));
  margin: 24px 0 4px;
}
</style>
